import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Plus,
  Edit2,
  Trash2,
  Package,
  Eye,
  EyeOff,
  ShieldOff,
  Layers,
  BookOpen,
} from "lucide-react";
import { itemTypesApi, type ItemType } from "@/lib/api/item-types";
import { useItemTypes, ITEM_TYPES_QUERY_KEY } from "@/hooks/useItemTypes";
import { cn } from "@/lib/utils";

const ItemTypesSettingsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: allItemTypes = [], isLoading: loading } = useItemTypes(true, {
    enabled: isTenantAdmin,
  });
  const itemTypes = includeInactive ? allItemTypes : allItemTypes.filter((t) => t.isActive);
  const stats = {
    active: allItemTypes.filter((t) => t.isActive).length,
    inactive: allItemTypes.filter((t) => !t.isActive).length,
    total: allItemTypes.length,
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemTypeToDelete, setItemTypeToDelete] = useState<ItemType | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleEdit = (itemType: ItemType) => {
    navigate(`/setup/item-types/${itemType.id}/edit`);
  };

  const openDeleteDialog = (itemType: ItemType) => {
    setItemTypeToDelete(itemType);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!itemTypeToDelete) return;

    try {
      setDeleting(true);
      const hardDelete = includeInactive || !itemTypeToDelete.isActive;
      await itemTypesApi.delete(itemTypeToDelete.id, hardDelete);

      const actionWord = hardDelete ? "permanently deleted" : "deactivated";
      toast.success(`"${itemTypeToDelete.name}" has been ${actionWord}.`);
      queryClient.invalidateQueries(ITEM_TYPES_QUERY_KEY);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to delete item type. Please try again.");
    } finally {
      setDeleting(false);
      setItemTypeToDelete(null);
    }
  };

  const handleNewClick = () => {
    navigate("/setup/item-types/new");
  };

  const filteredItemTypes = itemTypes.filter(
    (itemType) =>
      itemType.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      itemType.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      itemType.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage item types.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Item Types"
          description="Define categories of library materials and their borrowing rules"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Item Types" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by name or code..."
        />

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                <p className="text-sm text-gray-500">Active Types</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50">
                <EyeOff className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
                <p className="text-sm text-gray-500">Inactive Types</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-50">
                <Layers className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Types</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col justify-between gap-4 mb-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <Checkbox
              id="includeInactive"
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(checked === true)}
            />
            <Label
              htmlFor="includeInactive"
              className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
            >
              {includeInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Show inactive types
            </Label>
          </div>
          <Button onClick={handleNewClick} className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Item Type
          </Button>
        </div>

        {/* Item Types List */}
        <Card className="overflow-hidden bg-white shadow-sm">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading item types..." />
            </div>
          ) : filteredItemTypes.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <Package className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery ? "No matching item types" : "No item types yet"}
              </h3>
              <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                {searchQuery
                  ? "Try a different search term or check for typos."
                  : "Create your first item type to categorize library materials."}
              </p>
              {!searchQuery && (
                <Button onClick={handleNewClick} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Item Type
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredItemTypes.map((itemType) => (
                <div
                  key={itemType.id}
                  className={cn(
                    "p-5 hover:bg-gray-50 transition-colors",
                    !itemType.isActive && "bg-gray-50/50"
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{itemType.name}</h3>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono rounded">
                          {itemType.code}
                        </span>
                        <span
                          className={cn(
                            "px-2 py-0.5 rounded text-xs font-medium",
                            itemType.isBorrowable
                              ? "bg-primary/10 text-primary"
                              : "bg-gray-200 text-gray-600"
                          )}
                        >
                          {itemType.isBorrowable ? "Borrowable" : "Not Borrowable"}
                        </span>
                        <span
                          className={cn(
                            "px-2.5 py-0.5 rounded-full text-xs font-medium",
                            itemType.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-600"
                          )}
                        >
                          {itemType.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {itemType.description && (
                        <p className="mb-2 text-sm text-gray-500 line-clamp-2">
                          {itemType.description}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        {itemType.defaultLocation && (
                          <span>Location: {itemType.defaultLocation.name}</span>
                        )}
                        {itemType.defaultCollection && (
                          <span>Collection: {itemType.defaultCollection.name}</span>
                        )}
                        {itemType.allowedPatronTypes && itemType.allowedPatronTypes.length > 0 && (
                          <span>
                            Restricted to {itemType.allowedPatronTypes.length} patron type
                            {itemType.allowedPatronTypes.length !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(itemType)}
                        className="gap-1.5"
                      >
                        <Edit2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDeleteDialog(itemType)}
                        className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {itemType.isActive ? "Deactivate" : "Delete"}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Help Text */}
        <div className="p-4 mt-6 bg-white border border-gray-200 rounded-lg">
          <h4 className="mb-1 font-medium text-gray-900">What are item types?</h4>
          <p className="text-sm text-gray-600">
            Item types categorize your library materials (like "Book," "Magazine," or "DVD") and
            define their borrowing behavior. Each type can have its own loan period, fine rates, and
            restrictions on who can borrow them.
          </p>
        </div>
      </PageContainer>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={
          itemTypeToDelete?.isActive
            ? `Deactivate "${itemTypeToDelete?.name}"?`
            : `Delete "${itemTypeToDelete?.name}"?`
        }
        description={
          itemTypeToDelete?.isActive ? (
            <>
              <p className="mb-2">
                Deactivating this item type will hide it from selection lists. Existing items of
                this type will not be affected.
              </p>
              <p className="font-medium text-gray-600">You can reactivate it later if needed.</p>
            </>
          ) : (
            <>
              <p className="mb-2">
                This will <strong>permanently delete</strong> this item type. This action cannot be
                undone.
              </p>
              <p className="font-medium text-red-600">
                Make sure no items are currently using this type.
              </p>
            </>
          )
        }
        confirmLabel={itemTypeToDelete?.isActive ? "Deactivate" : "Delete Permanently"}
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        variant="danger"
        loading={deleting}
      />
    </AppLayout>
  );
};

export default ItemTypesSettingsPage;
